import { useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Link,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MDButton from "components/MDButton";
import logo from "../../assets/images/SalesHive.png";
import DemoForm from "./DemoForm";
import { publicLinks, publicNavGroups } from "./publicLinks";

export default function Navbar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDemoModal, setOpenDemoModal] = useState(false);
  const [menu, setMenu] = useState(null);
  const openDemo = () => {
    setMobileOpen(false);
    setOpenDemoModal(true);
  };
  const accountLinks = [
    { label: "Login", href: publicLinks.login },
    { label: "Register", href: publicLinks.register },
  ];
  const navButton = {
    minHeight: 44,
    px: 1.5,
    textTransform: "none",
    whiteSpace: "nowrap",
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      component="header"
      sx={{
        background: "rgba(255,255,255,.96)",
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid #e2e8f0",
        py: 1,
      }}
    >
      <Toolbar
        component="nav"
        aria-label="Public navigation"
        sx={{
          justifyContent: "space-between",
          gap: 2,
          px: { xs: 2, md: 4 },
          minWidth: 0,
        }}
      >
        <Link
          href={publicLinks.home}
          aria-label="SalesHive home"
          sx={{ display: "flex", flexShrink: 0 }}
        >
          <Box
            component="img"
            src={logo}
            alt="SalesHive CRM"
            sx={{
              width: { xs: 140, lg: 180 },
              maxWidth: "100%",
              objectFit: "contain",
            }}
          />
        </Link>
        {!isMobile ? (
          <Box display="flex" alignItems="center" gap={0.5}>
            <MDButton
              href={publicLinks.about}
              target="_blank"
              rel="noopener noreferrer"
              variant="text"
              color="info"
              sx={navButton}
            >
              About Us
            </MDButton>
            {publicNavGroups.map((group) => (
              <MDButton
                key={group.label}
                id={`public-${group.label.toLowerCase()}-button`}
                aria-haspopup="menu"
                aria-expanded={menu?.label === group.label}
                aria-controls={
                  menu?.label === group.label
                    ? "public-navigation-menu"
                    : undefined
                }
                onClick={(event) =>
                  setMenu({ ...group, anchor: event.currentTarget })
                }
                variant="text"
                color="info"
                endIcon={<ExpandMoreIcon />}
                sx={navButton}
              >
                {group.label}
              </MDButton>
            ))}
            {accountLinks.map((link) => (
              <MDButton
                key={link.label}
                href={link.href}
                variant={link.label === "Register" ? "outlined" : "text"}
                color="info"
                sx={navButton}
              >
                {link.label}
              </MDButton>
            ))}
            <MDButton
              onClick={openDemo}
              variant="outlined"
              color="success"
              sx={navButton}
            >
              Book a Demo
            </MDButton>
          </Box>
        ) : (
          <IconButton
            aria-label="Open navigation"
            aria-expanded={mobileOpen}
            aria-controls={mobileOpen ? "public-mobile-navigation" : undefined}
            onClick={() => setMobileOpen(true)}
            color="info"
            sx={{ width: 44, height: 44 }}
          >
            <MenuIcon />
          </IconButton>
        )}
      </Toolbar>
      <Menu
        id="public-navigation-menu"
        anchorEl={menu?.anchor}
        open={!isMobile && Boolean(menu)}
        onClose={() => setMenu(null)}
        MenuListProps={{
          "aria-labelledby": menu
            ? `public-${menu.label.toLowerCase()}-button`
            : undefined,
        }}
        PaperProps={{ sx: { maxWidth: "calc(100vw - 32px)" } }}
      >
        {menu?.links.map((link) => (
          <MenuItem
            key={link.href}
            component="a"
            href={link.href}
            onClick={() => setMenu(null)}
            sx={{ minHeight: 44, whiteSpace: "normal", fontSize: 14 }}
          >
            {link.label}
          </MenuItem>
        ))}
      </Menu>
      <Drawer
        anchor="right"
        open={isMobile && mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: "min(340px, 100vw)",
            maxWidth: "100%",
            p: 2,
            boxSizing: "border-box",
          },
        }}
      >
        <Box display="flex" justifyContent="flex-end">
          <IconButton
            aria-label="Close navigation"
            onClick={() => setMobileOpen(false)}
            sx={{ width: 44, height: 44 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          component="nav"
          id="public-mobile-navigation"
          aria-label="Mobile public navigation"
        >
          <List disablePadding>
            <ListItemButton
              component="a"
              href={publicLinks.about}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileOpen(false)}
            >
              <ListItemText primary="About Us" />
            </ListItemButton>
            {publicNavGroups.map((group) => (
              <li key={group.label}>
                <List
                  disablePadding
                  subheader={
                    <ListSubheader disableSticky>{group.label}</ListSubheader>
                  }
                >
                  {group.links.map((link) => (
                    <ListItemButton
                      key={link.href}
                      component="a"
                      href={link.href}
                      onClick={() => setMobileOpen(false)}
                      sx={{ minHeight: 44, pl: 3 }}
                    >
                      <ListItemText
                        primary={link.label}
                        primaryTypographyProps={{
                          fontSize: 14,
                          overflowWrap: "anywhere",
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </li>
            ))}
            {accountLinks.map((link) => (
              <ListItemButton
                key={link.href}
                component="a"
                href={link.href}
                onClick={() => setMobileOpen(false)}
                sx={{ minHeight: 44 }}
              >
                <ListItemText primary={link.label} />
              </ListItemButton>
            ))}
            <ListItemButton onClick={openDemo} sx={{ minHeight: 44 }}>
              <ListItemText primary="Book a Demo" />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>
      <DemoForm open={openDemoModal} onClose={() => setOpenDemoModal(false)} />
    </AppBar>
  );
}
